/**
 * Spoken or Text Only (feature guide)
 *
 * Screenshots were taken from the Sokuji v0.44.0 desktop app.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  "pageTitle": "Spoken or Text Only",
  "backLink": "Back to Documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Overview",
    "content": "Every translation is shown as text. Two switches in the <strong>Speech</strong> block decide which ones are also read aloud: <strong>Translation the other side hears</strong> speaks your translation to the other side, and <strong>Translation I hear</strong> speaks the other side’s translation to you. Switch one off when subtitles are enough in that direction, for example to follow a meeting or to caption your own talk."
  },
  "steps": [
    {
      "title": "Find the switches",
      "content": "In Settings, open the <strong>Speech</strong> block: it is in Quick settings, and in Advanced on the <strong>Audio</strong> tab. Both switches are fixed while a session runs, and say “Fixed for this session; stop it to change.”",
      "screenshot": "01-text-only.png"
    },
    {
      "title": "Translation the other side hears",
      "content": "On by default, in Me and Both modes: your translation is read aloud into the virtual microphone, so the other side hears it in the meeting. Switch it off for subtitles only: nothing reaches the virtual microphone, and <strong>I hear it too</strong> and <strong>Passthrough</strong> are greyed and silent. In Other mode the row is greyed."
    },
    {
      "title": "Translation I hear",
      "content": "Used in Other and Both modes, and off by default in a meeting. Switch it on to have the other side’s translation read aloud to you, on the device and channel you pick in that row. In the desktop app it needs one application as the <a href=\"/docs/features/participant-audio\">Other’s audio</a> source, for example Microsoft Teams: with <strong>System Audio (All Applications)</strong> the row is greyed, because the translation would be captured and translated again. In the browser extension it is always available. In Me mode the row is greyed."
    },
    {
      "title": "Face-to-face",
      "content": "With the other person beside you (Both, beside me), Translation I hear is on by default, and each translation plays in one earbud: Translation the other side hears in the other person’s ear, not into the virtual microphone, and Translation I hear in yours. I hear it too and Passthrough are not offered there."
    }
  ],
  "tips": {
    "title": "Tips",
    "items": [
      "With services that charge for generated speech, such as KizunaAI, each spoken direction costs more. On KizunaAI, Translation I hear adds a second spoken stream, and a session needs a higher balance to start.",
      "Where the service offers a choice of voice, each spoken row shows the voice it reads in. Click its name to change it.",
      "With Translation the other side hears off, the other side hears nothing from Sokuji. To let them read along, share the subtitle window."
    ]
  },
  "faq": {
    "title": "Frequently Asked Questions",
    "items": [
      {
        "question": "Where did Text Only go?",
        "answer": "It is now Translation the other side hears, the other way round: Text Only on is Translation the other side hears off. It moved from Translation Languages to the Speech block."
      },
      {
        "question": "Why can’t I change a switch?",
        "answer": "Both are fixed while a session runs: stop it first. Translation the other side hears is greyed in Other mode and Translation I hear in Me mode, since that direction is not translated. In the desktop app, Translation I hear is also greyed while Other’s audio is System Audio (All Applications): pick the application instead."
      },
      {
        "question": "Does switching speech off stop all speech costs?",
        "answer": "For most services, yes: no speech is generated. With Google Gemini, audio may still be generated and billed; Sokuji just does not play it."
      }
    ]
  },
  "troubleshooting": {
    "title": "Troubleshooting",
    "content": "Still stuck? Send us feedback from the account menu, or write to <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. You can also ask in <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const zh: TutorialData = {
  "pageTitle": "朗读或仅文字",
  "backLink": "返回文档",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概述",
    "content": "每条翻译都会以文字显示。<strong>语音</strong>区块中的两个开关决定哪些翻译还会被朗读出来：<strong>对方听到的翻译</strong>把你的翻译朗读给对方，<strong>我听到的翻译</strong>把对方的翻译朗读给你。某个方向只需要字幕时就把它关掉，例如用来跟上会议，或为你自己的演讲添加字幕。"
  },
  "steps": [
    {
      "title": "找到这两个开关",
      "content": "在设置中打开<strong>语音</strong>区块：它位于“快捷”设置中，也位于“高级”的<strong>音频</strong>标签页。会话运行期间这两个开关都是固定的，会显示“本次会话已固定，停止后可改。”",
      "screenshot": "01-text-only.png"
    },
    {
      "title": "对方听到的翻译",
      "content": "在“我”和“两者”模式下默认开启：你的翻译会朗读到虚拟麦克风中，让对方在会议里听到。关闭后只显示字幕：虚拟麦克风不会收到任何声音，<strong>我也听</strong>和<strong>原声直通</strong>会变灰且不发声。在“对方”模式下这一行是灰色的。"
    },
    {
      "title": "我听到的翻译",
      "content": "用于“对方”和“两者”模式，在线会议时默认关闭。开启后，对方的翻译会朗读给你听，在这一行选定的设备和声道上播放。在桌面应用中，它需要把某一个应用设为<a href=\"/docs/features/participant-audio\">对方音频</a>的来源，例如 Microsoft Teams：选择 <strong>System Audio (All Applications)</strong> 时这一行是灰色的，因为翻译会被再次捕获并翻译。在浏览器扩展中它总是可用。在“我”模式下这一行是灰色的。"
    },
    {
      "title": "面对面",
      "content": "对方就在你身边时（两者，就在身边），“我听到的翻译”默认开启，每路翻译在一只耳机里播放：“对方听到的翻译”在对方的耳机里播放，而不是送进虚拟麦克风；“我听到的翻译”在你的耳机里播放。这时不提供“我也听”和原声直通。"
    }
  ],
  "tips": {
    "title": "提示",
    "items": [
      "对于按生成语音收费的服务（例如 KizunaAI），每多朗读一个方向，费用就更高。在 KizunaAI 上，“我听到的翻译”会增加第二路语音，开始会话所需的余额也更高。",
      "如果服务提供多种声音可选，每个朗读的行都会显示它使用的声音。点击声音名称即可更改。",
      "关闭“对方听到的翻译”后，对方从 Sokuji 听不到任何声音。如果想让他们一起阅读，可以共享字幕窗口。"
    ]
  },
  "faq": {
    "title": "常见问题",
    "items": [
      {
        "question": "“仅文本”去哪儿了？",
        "answer": "它现在是“对方听到的翻译”，含义正好相反：开启仅文本就等于关闭“对方听到的翻译”。它已从“翻译语言”下方移到语音区块。"
      },
      {
        "question": "为什么无法更改某个开关？",
        "answer": "会话运行期间两个开关都是固定的：请先停止会话。“对方听到的翻译”在“对方”模式下是灰色的，“我听到的翻译”在“我”模式下是灰色的，因为那个方向不翻译。在桌面应用中，当对方音频为 System Audio (All Applications) 时，“我听到的翻译”也是灰色的：请改为选择具体的应用。"
      },
      {
        "question": "关闭朗读能免除所有语音费用吗？",
        "answer": "对大多数服务来说可以：不会生成任何语音。使用 Google Gemini 时，仍可能生成音频并计费，只是 Sokuji 不会播放它。"
      }
    ]
  },
  "troubleshooting": {
    "title": "故障排除",
    "content": "仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>。你也可以在 <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> 中提问。"
  }
};

const ja: TutorialData = {
  "pageTitle": "読み上げまたはテキストのみ",
  "backLink": "ドキュメントに戻る",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概要",
    "content": "翻訳はすべてテキストで表示されます。どの翻訳を読み上げるかは、<strong>音声</strong>ブロックの 2 つのスイッチで決まります。<strong>相手に聞こえる翻訳</strong>はあなたの翻訳を相手に向けて読み上げ、<strong>自分に聞こえる翻訳</strong>は相手の翻訳をあなたに向けて読み上げます。会議の内容を追う場合や、自分の講演に字幕を付ける場合など、その方向は字幕だけで十分なときはオフにしてください。"
  },
  "steps": [
    {
      "title": "スイッチの場所",
      "content": "設定で<strong>音声</strong>ブロックを開きます。「クイック」設定のほか、「詳細」の<strong>オーディオ</strong>タブにもあります。どちらのスイッチもセッションの実行中は固定され、「このセッションでは固定されています。変更するには停止してください。」と表示されます。",
      "screenshot": "01-text-only.png"
    },
    {
      "title": "相手に聞こえる翻訳",
      "content": "自分モードと両方モードで、既定ではオンです。あなたの翻訳が仮想マイクに向けて読み上げられ、相手は会議の中でそれを聞きます。字幕だけにするにはオフにします。仮想マイクには何も届かず、<strong>自分にも再生</strong>と<strong>原音パススルー</strong>はグレー表示になり、音も出ません。相手モードではこの行はグレー表示です。"
    },
    {
      "title": "自分に聞こえる翻訳",
      "content": "相手モードと両方モードで使い、オンライン会議では既定でオフです。オンにすると、相手の翻訳があなたに向けて読み上げられ、この行で選んだデバイスとチャンネルで再生されます。デスクトップアプリでは、<a href=\"/docs/features/participant-audio\">相手の音声</a>のソースに 1 つのアプリ（例：Microsoft Teams）を選ぶ必要があります。<strong>System Audio (All Applications)</strong> を選んでいると、翻訳がもう一度キャプチャされて再翻訳されてしまうため、この行はグレー表示になります。ブラウザー拡張機能では常に使えます。自分モードではこの行はグレー表示です。"
    },
    {
      "title": "対面",
      "content": "相手が隣にいる場合（両方、隣にいる）は、「自分に聞こえる翻訳」が既定でオンになり、それぞれの翻訳が片耳のイヤホンで再生されます。「相手に聞こえる翻訳」は仮想マイクではなく相手の耳に、「自分に聞こえる翻訳」はあなたの耳に届きます。対面では「自分にも再生」と「原音パススルー」は表示されません。"
    }
  ],
  "tips": {
    "title": "ヒント",
    "items": [
      "KizunaAI のように生成した音声に料金がかかるサービスでは、読み上げる方向が増えるほど料金も上がります。KizunaAI では、「自分に聞こえる翻訳」で読み上げの音声がもう 1 本増え、セッションの開始に必要な残高も高くなります。",
      "サービスで声を選べる場合、読み上げる各行には読み上げに使う声が表示されます。その名前をクリックすると変更できます。",
      "「相手に聞こえる翻訳」がオフのときは、相手には Sokuji から何も聞こえません。相手にも読んでもらうには、字幕ウィンドウを共有してください。"
    ]
  },
  "faq": {
    "title": "よくある質問",
    "items": [
      {
        "question": "テキストのみはどこへ行きましたか？",
        "answer": "現在は「相手に聞こえる翻訳」になり、オン・オフが逆になりました。テキストのみがオンの状態は、「相手に聞こえる翻訳」がオフの状態に当たります。場所も「翻訳言語」から「音声」ブロックに移りました。"
      },
      {
        "question": "スイッチを変更できないのはなぜですか？",
        "answer": "どちらのスイッチもセッションの実行中は固定されるので、先にセッションを停止してください。相手モードでは「相手に聞こえる翻訳」が、自分モードでは「自分に聞こえる翻訳」がグレー表示になります。その方向は翻訳されないためです。デスクトップアプリでは、相手の音声が System Audio (All Applications) の間も「自分に聞こえる翻訳」はグレー表示になります。代わりにアプリを選んでください。"
      },
      {
        "question": "読み上げをオフにすれば音声の料金はすべてかからなくなりますか？",
        "answer": "ほとんどのサービスでは、はい。音声は生成されません。Google Gemini では音声が生成されて課金されることがあり、Sokuji がそれを再生しないだけです。"
      }
    ]
  },
  "troubleshooting": {
    "title": "トラブルシューティング",
    "content": "それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a> までご連絡ください。<a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> で質問することもできます。"
  }
};

const ko: TutorialData = {
  "pageTitle": "음성 또는 텍스트만",
  "backLink": "문서로 돌아가기",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "개요",
    "content": "모든 번역은 텍스트로 표시됩니다. 어떤 번역을 음성으로도 읽어 줄지는 <strong>음성</strong> 블록의 두 스위치가 정합니다. <strong>상대방에게 들리는 번역</strong>은 내 번역을 상대방에게 읽어 주고, <strong>내가 듣는 번역</strong>은 상대방의 번역을 나에게 읽어 줍니다. 회의를 따라가거나 내 발표에 자막을 달 때처럼 그 방향은 자막만으로 충분할 때 꺼 두세요."
  },
  "steps": [
    {
      "title": "스위치 찾기",
      "content": "설정에서 <strong>음성</strong> 블록을 여세요. “빠른” 설정에 있고, “고급”의 <strong>오디오</strong> 탭에도 있습니다. 두 스위치 모두 세션이 실행되는 동안 고정되며, “이 세션에서는 고정되어 있습니다. 바꾸려면 중지하세요.”라고 표시됩니다.",
      "screenshot": "01-text-only.png"
    },
    {
      "title": "상대방에게 들리는 번역",
      "content": "나 모드와 모두 모드에서 기본적으로 켜져 있습니다. 내 번역을 가상 마이크로 읽어 주므로 상대방이 회의에서 듣습니다. 자막만 쓰려면 끄세요. 가상 마이크로는 아무것도 전달되지 않으며, <strong>나도 듣기</strong>와 <strong>원음 패스스루</strong>는 회색으로 표시되고 소리가 나지 않습니다. 상대방 모드에서는 이 행이 회색으로 표시됩니다."
    },
    {
      "title": "내가 듣는 번역",
      "content": "상대방 모드와 모두 모드에서 사용하며, 온라인 회의에서는 기본적으로 꺼져 있습니다. 켜면 상대방의 번역을 나에게 읽어 주며, 이 행에서 고른 장치와 채널로 재생됩니다. 데스크톱 앱에서는 <a href=\"/docs/features/participant-audio\">상대방 오디오</a> 소스로 앱 하나(예: Microsoft Teams)를 골라야 합니다. <strong>System Audio (All Applications)</strong>를 고르면 번역이 다시 캡처되어 또 번역되므로 이 행이 회색으로 표시됩니다. 브라우저 확장 프로그램에서는 항상 사용할 수 있습니다. 나 모드에서는 이 행이 회색으로 표시됩니다."
    },
    {
      "title": "대면 대화",
      "content": "상대방이 내 옆에 있으면(모두, 내 옆) “내가 듣는 번역”이 기본적으로 켜지고, 각 번역은 이어버드 한쪽에서 재생됩니다. “상대방에게 들리는 번역”은 가상 마이크가 아니라 상대방의 귀에, “내가 듣는 번역”은 내 귀에 재생됩니다. 이때는 “나도 듣기”와 “원음 패스스루”가 제공되지 않습니다."
    }
  ],
  "tips": {
    "title": "팁",
    "items": [
      "KizunaAI처럼 생성된 음성에 요금을 부과하는 서비스에서는 음성으로 읽어 주는 방향이 늘어날수록 비용이 늘어납니다. KizunaAI에서는 “내가 듣는 번역”이 두 번째 음성 스트림을 추가하므로, 세션을 시작하는 데 더 많은 잔액이 필요합니다.",
      "서비스에서 목소리를 고를 수 있으면, 음성으로 읽어 주는 각 행에 읽는 데 쓰는 목소리가 표시됩니다. 그 이름을 클릭하면 바꿀 수 있습니다.",
      "“상대방에게 들리는 번역”이 꺼져 있으면 상대방은 Sokuji에서 아무 소리도 듣지 못합니다. 상대방도 함께 읽게 하려면 자막 창을 공유하세요."
    ]
  },
  "faq": {
    "title": "자주 묻는 질문",
    "items": [
      {
        "question": "텍스트만은 어디로 갔나요?",
        "answer": "이제 “상대방에게 들리는 번역”이 되었으며, 켜고 끄는 의미가 반대입니다. 텍스트만을 켠 상태가 “상대방에게 들리는 번역”을 끈 상태입니다. 위치도 “번역 언어”에서 “음성” 블록으로 옮겨졌습니다."
      },
      {
        "question": "스위치를 바꿀 수 없는 이유는 무엇인가요?",
        "answer": "두 스위치 모두 세션이 실행되는 동안 고정되므로 먼저 세션을 중지하세요. 상대방 모드에서는 “상대방에게 들리는 번역”이, 나 모드에서는 “내가 듣는 번역”이 회색으로 표시됩니다. 그 방향은 번역하지 않기 때문입니다. 데스크톱 앱에서는 상대방 오디오가 System Audio (All Applications)인 동안에도 “내가 듣는 번역”이 회색으로 표시됩니다. 대신 앱을 고르세요."
      },
      {
        "question": "음성을 끄면 음성 비용이 전혀 들지 않나요?",
        "answer": "대부분의 서비스에서는 그렇습니다. 음성이 생성되지 않습니다. Google Gemini에서는 오디오가 여전히 생성되어 요금이 부과될 수 있으며, Sokuji가 재생하지 않을 뿐입니다."
      }
    ]
  },
  "troubleshooting": {
    "title": "문제 해결",
    "content": "그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>에서 질문할 수도 있습니다."
  }
};

const es: TutorialData = {
  "pageTitle": "Con voz o solo texto",
  "backLink": "Volver a la documentación",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Descripción general",
    "content": "Todas las traducciones se muestran como texto. Dos interruptores del bloque <strong>Voz</strong> deciden cuáles se leen también en voz alta: <strong>Traducción que oye la otra parte</strong> lee tu traducción a la otra parte, y <strong>Traducción que yo oigo</strong> te lee a ti la traducción de la otra parte. Desactiva uno cuando en esa dirección te basten los subtítulos, por ejemplo para seguir una reunión o para subtitular tu propia charla."
  },
  "steps": [
    {
      "title": "Encuentra los interruptores",
      "content": "En Configuración, abre el bloque <strong>Voz</strong>: está en Rápido y, en Avanzado, en la pestaña <strong>Audio</strong>. Los dos interruptores quedan fijos mientras hay una sesión en marcha e indican «Fijado para esta sesión; detén la sesión para cambiarlo.»",
      "screenshot": "01-text-only.png"
    },
    {
      "title": "Traducción que oye la otra parte",
      "content": "Activada de forma predeterminada, en los modos Yo y Ambos: tu traducción se lee en voz alta en el micrófono virtual, así que la otra parte la oye en la reunión. Desactívala para tener solo subtítulos: no llega nada al micrófono virtual, y <strong>Yo también la oigo</strong> y <strong>Paso directo</strong> aparecen en gris y en silencio. En el modo Otro, la fila aparece en gris."
    },
    {
      "title": "Traducción que yo oigo",
      "content": "Se usa en los modos Otro y Ambos, y en una reunión está desactivada de forma predeterminada. Actívala para que se te lea en voz alta la traducción de la otra parte, en el dispositivo y el canal que elijas en esa fila. En la aplicación de escritorio necesita una aplicación concreta como fuente del <a href=\"/docs/features/participant-audio\">Audio del otro</a>, por ejemplo Microsoft Teams: con <strong>System Audio (All Applications)</strong> la fila aparece en gris, porque la traducción se capturaría y se volvería a traducir. En la extensión de navegador siempre está disponible. En el modo Yo, la fila aparece en gris."
    },
    {
      "title": "Cara a cara",
      "content": "Con la otra persona a tu lado (Ambos, a mi lado), Traducción que yo oigo está activada de forma predeterminada, y cada traducción suena en un auricular: Traducción que oye la otra parte en el oído de la otra persona, no en el micrófono virtual, y Traducción que yo oigo en el tuyo. Yo también la oigo y Paso directo no se ofrecen ahí."
    }
  ],
  "tips": {
    "title": "Consejos",
    "items": [
      "Con servicios que cobran por la voz generada, como KizunaAI, cada dirección hablada cuesta más. En KizunaAI, Traducción que yo oigo añade un segundo flujo de voz, y una sesión necesita más saldo para empezar.",
      "Cuando el servicio permite elegir la voz, cada fila hablada muestra la voz con la que lee. Haz clic en su nombre para cambiarla.",
      "Con Traducción que oye la otra parte desactivada, la otra parte no oye nada de Sokuji. Para que pueda leer la traducción, comparte la ventana de subtítulos."
    ]
  },
  "faq": {
    "title": "Preguntas frecuentes",
    "items": [
      {
        "question": "¿Dónde está ahora Solo texto?",
        "answer": "Ahora es Traducción que oye la otra parte, al revés: Solo texto activado equivale a Traducción que oye la otra parte desactivada. Ha pasado de Idiomas de Traducción al bloque Voz."
      },
      {
        "question": "¿Por qué no puedo cambiar un interruptor?",
        "answer": "Los dos quedan fijos mientras hay una sesión en marcha: detenla primero. Traducción que oye la otra parte aparece en gris en el modo Otro, y Traducción que yo oigo en el modo Yo, porque esa dirección no se traduce. En la aplicación de escritorio, Traducción que yo oigo también aparece en gris mientras el Audio del otro es System Audio (All Applications): elige la aplicación en su lugar."
      },
      {
        "question": "¿Desactivar la voz elimina todos los costes de voz?",
        "answer": "En la mayoría de los servicios, sí: no se genera voz. Con Google Gemini, puede que se siga generando y cobrando audio; Sokuji simplemente no lo reproduce."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solución de problemas",
    "content": "¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. También puedes preguntar en <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const fr: TutorialData = {
  "pageTitle": "Voix ou texte uniquement",
  "backLink": "Retour à la documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Aperçu",
    "content": "Chaque traduction est affichée sous forme de texte. Deux interrupteurs du bloc <strong>Voix</strong> décident lesquelles sont aussi lues à voix haute : <strong>Traduction entendue par l'autre</strong> lit votre traduction à votre interlocuteur, et <strong>Traduction que j'entends</strong> vous lit la traduction de votre interlocuteur. Désactivez-en un quand les sous-titres suffisent dans ce sens, par exemple pour suivre une réunion ou sous-titrer votre propre intervention."
  },
  "steps": [
    {
      "title": "Trouvez les interrupteurs",
      "content": "Dans les Paramètres, ouvrez le bloc <strong>Voix</strong> : il se trouve dans Rapide, et dans Avancé sous l’onglet <strong>Audio</strong>. Les deux interrupteurs sont fixés pendant une session et indiquent « Fixé pour cette session ; arrêtez-la pour modifier. »",
      "screenshot": "01-text-only.png"
    },
    {
      "title": "Traduction entendue par l'autre",
      "content": "Activée par défaut, en modes Moi et Les deux : votre traduction est lue à voix haute dans le microphone virtuel, pour que votre interlocuteur l’entende dans la réunion. Désactivez-la pour n’avoir que des sous-titres : rien n’arrive au microphone virtuel, et <strong>Je l'entends aussi</strong> et <strong>Passage direct</strong> sont grisés et muets. En mode Autre, la ligne est grisée."
    },
    {
      "title": "Traduction que j'entends",
      "content": "Utilisée en modes Autre et Les deux, et désactivée par défaut en réunion. Activez-la pour que la traduction de votre interlocuteur vous soit lue à voix haute, sur le périphérique et le canal choisis sur cette ligne. Dans l’application de bureau, elle nécessite une seule application comme source d’<a href=\"/docs/features/participant-audio\">Audio de l'autre</a>, par exemple Microsoft Teams : avec <strong>System Audio (All Applications)</strong>, la ligne est grisée, car la traduction serait capturée et traduite de nouveau. Dans l’extension de navigateur, elle est toujours disponible. En mode Moi, la ligne est grisée."
    },
    {
      "title": "Face à face",
      "content": "Quand l’autre personne est à côté de vous (Les deux, à côté de moi), Traduction que j'entends est activée par défaut, et chaque traduction est lue dans un écouteur : Traduction entendue par l'autre dans l’oreille de l’autre personne, et non dans le microphone virtuel, et Traduction que j'entends dans la vôtre. Je l'entends aussi et Passage direct n’y sont pas proposés."
    }
  ],
  "tips": {
    "title": "Conseils",
    "items": [
      "Avec les services qui facturent la voix générée, comme KizunaAI, chaque sens lu à voix haute coûte plus cher. Sur KizunaAI, Traduction que j'entends ajoute un second flux vocal, et une session a besoin d’un solde plus élevé pour démarrer.",
      "Quand le service propose un choix de voix, chaque ligne lue à voix haute affiche la voix utilisée. Cliquez sur son nom pour la changer.",
      "Avec Traduction entendue par l'autre désactivée, votre interlocuteur n’entend rien de Sokuji. Pour qu’il puisse lire la traduction, partagez la fenêtre de sous-titres."
    ]
  },
  "faq": {
    "title": "Questions fréquentes",
    "items": [
      {
        "question": "Où est passé Texte uniquement ?",
        "answer": "C’est désormais Traduction entendue par l'autre, à l’inverse : Texte uniquement activé correspond à Traduction entendue par l'autre désactivée. Il est passé de Langues de Traduction au bloc Voix."
      },
      {
        "question": "Pourquoi ne puis-je pas changer un interrupteur ?",
        "answer": "Les deux sont fixés pendant une session : arrêtez-la d’abord. Traduction entendue par l'autre est grisée en mode Autre, et Traduction que j'entends en mode Moi, car ce sens n’est pas traduit. Dans l’application de bureau, Traduction que j'entends est aussi grisée tant que l’Audio de l'autre est System Audio (All Applications) : choisissez plutôt l’application."
      },
      {
        "question": "Désactiver la voix supprime-t-il tous les coûts de voix ?",
        "answer": "Pour la plupart des services, oui : aucune voix n’est générée. Avec Google Gemini, de l’audio peut encore être généré et facturé ; Sokuji ne le lit simplement pas."
      }
    ]
  },
  "troubleshooting": {
    "title": "Dépannage",
    "content": "Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ar: TutorialData = {
  "pageTitle": "منطوق أو نص فقط",
  "backLink": "العودة إلى التوثيق",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "نظرة عامة",
    "content": "تُعرض كل ترجمة كنص. ويحدد مفتاحان في قسم <strong>الكلام</strong> أيّها يُقرأ بصوت عالٍ أيضًا: <strong>الترجمة التي يسمعها الطرف الآخر</strong> تنطق ترجمتك للطرف الآخر، و<strong>الترجمة التي أسمعها</strong> تنطق لك ترجمة كلام الطرف الآخر. أوقف أحدهما عندما تكفي الترجمة النصية في ذلك الاتجاه، مثلًا لمتابعة اجتماع أو لإضافة ترجمة نصية إلى حديثك."
  },
  "steps": [
    {
      "title": "اعثر على المفتاحين",
      "content": "في الإعدادات، افتح قسم <strong>الكلام</strong>: تجده في إعدادات «سريع»، وفي «متقدم» ضمن علامة التبويب <strong>الصوت</strong>. يكون المفتاحان مثبّتين أثناء تشغيل الجلسة، ويظهر فيهما «مثبّت لهذه الجلسة؛ أوقفها لتغييره.»",
      "screenshot": "01-text-only.png"
    },
    {
      "title": "الترجمة التي يسمعها الطرف الآخر",
      "content": "مفعّلة افتراضيًا في وضعي أنا والكل: تُقرأ ترجمتك بصوت عالٍ في الميكروفون الافتراضي، فيسمعها الطرف الآخر في الاجتماع. أوقفها إذا أردت الترجمة النصية فقط: عندها لا يصل شيء إلى الميكروفون الافتراضي، ويصبح <strong>أسمعها أنا أيضًا</strong> و<strong>التمرير المباشر</strong> باللون الرمادي وصامتين. وفي وضع الآخر يكون الصف باللون الرمادي."
    },
    {
      "title": "الترجمة التي أسمعها",
      "content": "تُستخدم في وضعي الآخر والكل، وتكون متوقفة افتراضيًا في الاجتماع. فعّلها لتُقرأ لك ترجمة كلام الطرف الآخر بصوت عالٍ، على الجهاز والقناة اللذين تختارهما في ذلك الصف. في تطبيق سطح المكتب تحتاج إلى تطبيق واحد مصدرًا لـ<a href=\"/docs/features/participant-audio\">صوت الآخر</a>، مثل Microsoft Teams: فمع <strong>System Audio (All Applications)</strong> يكون الصف باللون الرمادي، لأن الترجمة ستُلتقط وتُترجم مرة أخرى. وفي امتداد المتصفح تكون متاحة دائمًا. وفي وضع أنا يكون الصف باللون الرمادي."
    },
    {
      "title": "وجهًا لوجه",
      "content": "عندما يكون الشخص الآخر بجانبك (الكل، بجانبي)، تكون «الترجمة التي أسمعها» مفعّلة افتراضيًا، وتُشغَّل كل ترجمة في سماعة أذن واحدة: «الترجمة التي يسمعها الطرف الآخر» في أذن الشخص الآخر، لا في الميكروفون الافتراضي، و«الترجمة التي أسمعها» في أذنك. ولا يُعرض هناك «أسمعها أنا أيضًا» ولا «التمرير المباشر»."
    }
  ],
  "tips": {
    "title": "نصائح",
    "items": [
      "مع الخدمات التي تحتسب رسومًا على الكلام المُولَّد، مثل KizunaAI، يكلّف كل اتجاه منطوق أكثر. وفي KizunaAI تضيف «الترجمة التي أسمعها» بثًا منطوقًا ثانيًا، وتحتاج الجلسة إلى رصيد أعلى لتبدأ.",
      "حيث تتيح الخدمة اختيار الصوت، يعرض كل صف منطوق الصوت الذي يقرأ به. انقر على اسمه لتغييره.",
      "عندما تكون «الترجمة التي يسمعها الطرف الآخر» متوقفة، لا يسمع الطرف الآخر شيئًا من Sokuji. ليتمكنوا من القراءة معك، شارك نافذة الترجمة النصية."
    ]
  },
  "faq": {
    "title": "الأسئلة الشائعة",
    "items": [
      {
        "question": "أين ذهب «نص فقط»؟",
        "answer": "أصبح الآن «الترجمة التي يسمعها الطرف الآخر» ولكن بالعكس: تفعيل «نص فقط» يعادل إيقاف «الترجمة التي يسمعها الطرف الآخر». وقد انتقل من «لغات الترجمة» إلى قسم الكلام."
      },
      {
        "question": "لماذا لا أستطيع تغيير أحد المفتاحين؟",
        "answer": "يكون المفتاحان مثبّتين أثناء تشغيل الجلسة: أوقفها أولًا. وتكون «الترجمة التي يسمعها الطرف الآخر» باللون الرمادي في وضع الآخر، و«الترجمة التي أسمعها» في وضع أنا، لأن ذلك الاتجاه لا يُترجَم. وفي تطبيق سطح المكتب تكون «الترجمة التي أسمعها» باللون الرمادي أيضًا ما دام صوت الآخر مضبوطًا على System Audio (All Applications): اختر التطبيق بدلًا من ذلك."
      },
      {
        "question": "هل يوقف إيقاف النطق كل تكاليف الكلام؟",
        "answer": "في معظم الخدمات، نعم: لا يُولَّد أي كلام. مع Google Gemini قد يُولَّد الصوت ويُحتسب رغم ذلك؛ كل ما في الأمر أن Sokuji لا يشغّله."
      }
    ]
  },
  "troubleshooting": {
    "title": "استكشاف الأخطاء وإصلاحها",
    "content": "هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const pt: TutorialData = {
  "pageTitle": "Com voz ou somente texto",
  "backLink": "Voltar à documentação",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Visão geral",
    "content": "Todas as traduções são mostradas como texto. Duas opções no bloco <strong>Voz</strong> definem quais também são lidas em voz alta: <strong>Tradução que o outro lado ouve</strong> fala a sua tradução para a outra pessoa, e <strong>Tradução que eu ouço</strong> fala para você a tradução da outra pessoa. Desative uma delas quando as legendas bastarem naquela direção, por exemplo para acompanhar uma reunião ou legendar a sua própria apresentação."
  },
  "steps": [
    {
      "title": "Encontre as opções",
      "content": "Em Configurações, abra o bloco <strong>Voz</strong>: ele está em Rápido e, em Avançado, na aba <strong>Áudio</strong>. As duas opções ficam fixas durante uma sessão e mostram “Fixo nesta sessão; pare-a para alterar.”",
      "screenshot": "01-text-only.png"
    },
    {
      "title": "Tradução que o outro lado ouve",
      "content": "Ativada por padrão, nos modos Eu e Ambos: a sua tradução é lida em voz alta no microfone virtual, então a outra pessoa a ouve na reunião. Desative-a para ter só legendas: nada chega ao microfone virtual, e <strong>Eu também ouço</strong> e <strong>Passagem direta</strong> ficam esmaecidas e em silêncio. No modo Outro, a linha fica esmaecida."
    },
    {
      "title": "Tradução que eu ouço",
      "content": "Usada nos modos Outro e Ambos, e desativada por padrão em uma reunião. Ative-a para que a tradução da outra pessoa seja lida em voz alta para você, no dispositivo e no canal que você escolher nessa linha. No aplicativo de desktop, ela precisa de um aplicativo específico como fonte do <a href=\"/docs/features/participant-audio\">Áudio do outro</a>, por exemplo o Microsoft Teams: com <strong>System Audio (All Applications)</strong>, a linha fica esmaecida, porque a tradução seria capturada e traduzida de novo. Na extensão de navegador, ela está sempre disponível. No modo Eu, a linha fica esmaecida."
    },
    {
      "title": "Cara a cara",
      "content": "Com a outra pessoa ao seu lado (Ambos, ao meu lado), Tradução que eu ouço vem ativada por padrão, e cada tradução toca em um dos fones: Tradução que o outro lado ouve no ouvido da outra pessoa, não no microfone virtual, e Tradução que eu ouço no seu. Eu também ouço e Passagem direta não são oferecidas ali."
    }
  ],
  "tips": {
    "title": "Dicas",
    "items": [
      "Com serviços que cobram pela fala gerada, como a KizunaAI, cada direção falada custa mais. Na KizunaAI, Tradução que eu ouço acrescenta um segundo fluxo de fala, e uma sessão precisa de um saldo maior para começar.",
      "Quando o serviço permite escolher a voz, cada linha falada mostra a voz com que lê. Clique no nome dela para trocá-la.",
      "Com Tradução que o outro lado ouve desativada, a outra pessoa não ouve nada do Sokuji. Para que ela possa ler a tradução, compartilhe a janela de legendas."
    ]
  },
  "faq": {
    "title": "Perguntas frequentes",
    "items": [
      {
        "question": "Para onde foi Somente texto?",
        "answer": "Agora é Tradução que o outro lado ouve, ao contrário: Somente texto ativado equivale a Tradução que o outro lado ouve desativada. A opção saiu de Idiomas de Tradução e foi para o bloco Voz."
      },
      {
        "question": "Por que não consigo alterar uma opção?",
        "answer": "As duas ficam fixas durante uma sessão: encerre-a primeiro. Tradução que o outro lado ouve fica esmaecida no modo Outro, e Tradução que eu ouço no modo Eu, porque essa direção não é traduzida. No aplicativo de desktop, Tradução que eu ouço também fica esmaecida enquanto o Áudio do outro é System Audio (All Applications): escolha o aplicativo em vez disso."
      },
      {
        "question": "Desativar a fala elimina todos os custos de fala?",
        "answer": "Na maioria dos serviços, sim: nenhuma fala é gerada. Com o Google Gemini, o áudio ainda pode ser gerado e cobrado; o Sokuji apenas não o reproduz."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solução de problemas",
    "content": "Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Você também pode perguntar no <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ru: TutorialData = {
  "pageTitle": "Озвучивание или только текст",
  "backLink": "Назад к документации",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Обзор",
    "content": "Каждый перевод показывается в виде текста. Два переключателя в блоке <strong>Речь</strong> определяют, какие из них ещё и озвучиваются: <strong>Перевод, который слышит собеседник</strong> зачитывает ваш перевод собеседнику, а <strong>Перевод, который слышу я</strong> зачитывает вам перевод собеседника. Выключите один из них, когда в этом направлении достаточно субтитров, например чтобы следить за встречей или субтитровать своё выступление."
  },
  "steps": [
    {
      "title": "Найдите переключатели",
      "content": "В настройках откройте блок <strong>Речь</strong>: он есть в режиме «Быстрый», а также в режиме «Расширенный» на вкладке <strong>Аудио</strong>. Оба переключателя зафиксированы, пока идёт сеанс, и на них написано: «Зафиксировано на эту сессию; остановите её, чтобы изменить.»",
      "screenshot": "01-text-only.png"
    },
    {
      "title": "Перевод, который слышит собеседник",
      "content": "Включён по умолчанию в режимах «Я» и «Оба»: ваш перевод зачитывается в виртуальный микрофон, и собеседник слышит его на встрече. Выключите его, чтобы оставить только субтитры: на виртуальный микрофон ничего не поступает, а <strong>Я тоже слышу</strong> и <strong>Passthrough</strong> становятся серыми и беззвучными. В режиме «Собеседник» эта строка серая."
    },
    {
      "title": "Перевод, который слышу я",
      "content": "Используется в режимах «Собеседник» и «Оба», на встрече по умолчанию выключен. Включите его, чтобы перевод собеседника зачитывался вам на устройстве и канале, выбранных в этой строке. В настольном приложении для этого источником <a href=\"/docs/features/participant-audio\">аудио собеседника</a> должно быть одно приложение, например Microsoft Teams: с <strong>System Audio (All Applications)</strong> строка серая, потому что перевод был бы захвачен и переведён снова. В расширении для браузера он доступен всегда. В режиме «Я» эта строка серая."
    },
    {
      "title": "Лицом к лицу",
      "content": "Когда собеседник рядом с вами («Оба», «Рядом со мной»), «Перевод, который слышу я» по умолчанию включён, и каждый перевод звучит в одном наушнике: «Перевод, который слышит собеседник» — в ухе собеседника (не в виртуальном микрофоне), «Перевод, который слышу я» — в вашем. «Я тоже слышу» и «Passthrough» там недоступны."
    }
  ],
  "tips": {
    "title": "Советы",
    "items": [
      "С сервисами, которые берут плату за синтез речи, например KizunaAI, каждое озвучиваемое направление стоит дороже. В KizunaAI «Перевод, который слышу я» добавляет второй озвучиваемый поток, и для запуска сеанса нужен больший баланс.",
      "Если сервис позволяет выбрать голос, каждая озвучиваемая строка показывает голос, которым она зачитывается. Нажмите на название голоса, чтобы его сменить.",
      "Когда «Перевод, который слышит собеседник» выключен, собеседник ничего не слышит от Sokuji. Чтобы он мог читать, покажите ему окно субтитров."
    ]
  },
  "faq": {
    "title": "Часто задаваемые вопросы",
    "items": [
      {
        "question": "Куда делось «Только текст»?",
        "answer": "Теперь это «Перевод, который слышит собеседник», только наоборот: включённое «Только текст» — это выключенный «Перевод, который слышит собеседник». Переключатель переехал из раздела «Языки перевода» в блок «Речь»."
      },
      {
        "question": "Почему я не могу изменить переключатель?",
        "answer": "Оба зафиксированы, пока идёт сеанс: сначала остановите его. «Перевод, который слышит собеседник» серый в режиме «Собеседник», а «Перевод, который слышу я» — в режиме «Я», потому что это направление не переводится. В настольном приложении «Перевод, который слышу я» также серый, пока в аудио собеседника выбрано System Audio (All Applications): выберите вместо этого приложение."
      },
      {
        "question": "Прекращаются ли все расходы на речь, если выключить озвучивание?",
        "answer": "Для большинства сервисов да: речь не синтезируется. С Google Gemini аудио всё равно может создаваться и оплачиваться; Sokuji просто его не воспроизводит."
      }
    ]
  },
  "troubleshooting": {
    "title": "Устранение неполадок",
    "content": "Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Также можно задать вопрос в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const de: TutorialData = {
  "pageTitle": "Gesprochen oder nur Text",
  "backLink": "Zurück zur Dokumentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Übersicht",
    "content": "Jede Übersetzung wird als Text angezeigt. Zwei Schalter im Bereich <strong>Sprache</strong> legen fest, welche davon auch vorgelesen werden: <strong>Übersetzung, die das Gegenüber hört</strong> spricht Ihre Übersetzung für Ihr Gegenüber, und <strong>Übersetzung, die ich höre</strong> spricht die Übersetzung Ihres Gegenübers für Sie. Schalten Sie einen davon aus, wenn in dieser Richtung Untertitel genügen, zum Beispiel um einem Meeting zu folgen oder Ihren eigenen Vortrag zu untertiteln."
  },
  "steps": [
    {
      "title": "Die Schalter finden",
      "content": "Öffnen Sie in den Einstellungen den Bereich <strong>Sprache</strong>: Sie finden ihn in der Ansicht „Schnell“ und in „Erweitert“ im Tab <strong>Audio</strong>. Beide Schalter sind festgelegt, solange eine Sitzung läuft, und zeigen „Für diese Sitzung festgelegt; zum Ändern stoppen.“",
      "screenshot": "01-text-only.png"
    },
    {
      "title": "Übersetzung, die das Gegenüber hört",
      "content": "Standardmäßig eingeschaltet, in den Modi „Ich“ und „Beides“: Ihre Übersetzung wird in das virtuelle Mikrofon gesprochen, sodass Ihr Gegenüber sie im Meeting hört. Für reine Untertitel schalten Sie sie aus: Dann kommt nichts am virtuellen Mikrofon an, und <strong>Ich höre es auch</strong> und <strong>Durchleitung</strong> sind ausgegraut und stumm. Im Modus „Gegenüber“ ist die Zeile ausgegraut."
    },
    {
      "title": "Übersetzung, die ich höre",
      "content": "Wird in den Modi „Gegenüber“ und „Beides“ verwendet und ist in einem Meeting standardmäßig aus. Schalten Sie sie ein, damit Ihnen die Übersetzung Ihres Gegenübers vorgelesen wird, auf dem Gerät und Kanal, die Sie in dieser Zeile wählen. In der Desktop-App braucht sie eine einzelne Anwendung als Quelle für <a href=\"/docs/features/participant-audio\">Audio des Gegenübers</a>, zum Beispiel Microsoft Teams: Mit <strong>System Audio (All Applications)</strong> ist die Zeile ausgegraut, weil die Übersetzung sonst aufgenommen und erneut übersetzt würde. In der Browsererweiterung ist sie immer verfügbar. Im Modus „Ich“ ist die Zeile ausgegraut."
    },
    {
      "title": "Von Angesicht zu Angesicht",
      "content": "Sitzt die andere Person neben Ihnen („Beides“, „Neben mir“), ist „Übersetzung, die ich höre“ standardmäßig eingeschaltet, und jede Übersetzung wird in einem Ohrhörer abgespielt: „Übersetzung, die das Gegenüber hört“ im Ohr der anderen Person statt im virtuellen Mikrofon und „Übersetzung, die ich höre“ in Ihrem. „Ich höre es auch“ und „Durchleitung“ werden dort nicht angeboten."
    }
  ],
  "tips": {
    "title": "Tipps",
    "items": [
      "Bei Diensten, die für erzeugte Sprache abrechnen, etwa KizunaAI, kostet jede gesprochene Richtung mehr. Bei KizunaAI fügt „Übersetzung, die ich höre“ einen zweiten gesprochenen Stream hinzu, und eine Sitzung braucht zum Starten ein höheres Guthaben.",
      "Wo der Dienst eine Auswahl an Stimmen bietet, zeigt jede gesprochene Zeile die Stimme, mit der sie vorliest. Klicken Sie auf ihren Namen, um sie zu ändern.",
      "Ist „Übersetzung, die das Gegenüber hört“ aus, hört Ihr Gegenüber nichts von Sokuji. Damit es mitlesen kann, geben Sie das Untertitelfenster frei."
    ]
  },
  "faq": {
    "title": "Häufig gestellte Fragen",
    "items": [
      {
        "question": "Wo ist „Nur Text“ geblieben?",
        "answer": "Es ist jetzt „Übersetzung, die das Gegenüber hört“, nur umgekehrt: „Nur Text“ ein entspricht „Übersetzung, die das Gegenüber hört“ aus. Es ist von „Übersetzungssprachen“ in den Bereich „Sprache“ umgezogen."
      },
      {
        "question": "Warum kann ich einen Schalter nicht ändern?",
        "answer": "Beide sind festgelegt, solange eine Sitzung läuft: Beenden Sie sie zuerst. „Übersetzung, die das Gegenüber hört“ ist im Modus „Gegenüber“ ausgegraut und „Übersetzung, die ich höre“ im Modus „Ich“, weil diese Richtung nicht übersetzt wird. In der Desktop-App ist „Übersetzung, die ich höre“ außerdem ausgegraut, solange das Audio des Gegenübers auf System Audio (All Applications) steht: Wählen Sie stattdessen die Anwendung."
      },
      {
        "question": "Entfallen alle Sprachkosten, wenn ich die Sprachausgabe ausschalte?",
        "answer": "Bei den meisten Diensten ja: Es wird keine Sprache erzeugt. Bei Google Gemini kann trotzdem Audio erzeugt und abgerechnet werden; Sokuji spielt es nur nicht ab."
      }
    ]
  },
  "troubleshooting": {
    "title": "Fehlerbehebung",
    "content": "Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Sie können auch in den <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> fragen."
  }
};

const uk: TutorialData = {
  "pageTitle": "Озвучування або лише текст",
  "backLink": "Назад до документації",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Огляд",
    "content": "Кожен переклад показується у вигляді тексту. Два перемикачі в блоці <strong>Мовлення</strong> визначають, які з них ще й озвучуються: <strong>Переклад, який чує співрозмовник</strong> зачитує ваш переклад співрозмовнику, а <strong>Переклад, який чую я</strong> зачитує вам переклад співрозмовника. Вимкніть один із них, коли в цьому напрямку достатньо субтитрів, наприклад щоб стежити за зустріччю або субтитрувати власний виступ."
  },
  "steps": [
    {
      "title": "Знайдіть перемикачі",
      "content": "У налаштуваннях відкрийте блок <strong>Мовлення</strong>: він є в режимі «Швидкий», а також у режимі «Розширений» на вкладці <strong>Аудіо</strong>. Обидва перемикачі зафіксовано, поки триває сеанс, і на них написано: «Зафіксовано на цю сесію; зупиніть її, щоб змінити.»",
      "screenshot": "01-text-only.png"
    },
    {
      "title": "Переклад, який чує співрозмовник",
      "content": "Увімкнено за замовчуванням у режимах «Я» та «Обидва»: ваш переклад зачитується у віртуальний мікрофон, і співрозмовник чує його на зустрічі. Вимкніть його, щоб залишити лише субтитри: на віртуальний мікрофон нічого не надходить, а <strong>Я теж чую</strong> і <strong>Passthrough</strong> стають сірими й беззвучними. У режимі «Співрозмовник» цей рядок сірий."
    },
    {
      "title": "Переклад, який чую я",
      "content": "Використовується в режимах «Співрозмовник» і «Обидва», на зустрічі за замовчуванням вимкнений. Увімкніть його, щоб переклад співрозмовника зачитувався вам на пристрої та каналі, вибраних у цьому рядку. У настільному застосунку для цього джерелом <a href=\"/docs/features/participant-audio\">аудіо співрозмовника</a> має бути один застосунок, наприклад Microsoft Teams: із <strong>System Audio (All Applications)</strong> рядок сірий, бо переклад було б захоплено й перекладено знову. У розширенні для браузера він доступний завжди. У режимі «Я» цей рядок сірий."
    },
    {
      "title": "Віч-на-віч",
      "content": "Коли співрозмовник поруч із вами («Обидва», «Поруч зі мною»), «Переклад, який чую я» за замовчуванням увімкнено, і кожен переклад звучить в одному навушнику: «Переклад, який чує співрозмовник» — у вусі співрозмовника (не у віртуальному мікрофоні), «Переклад, який чую я» — у вашому. «Я теж чую» і «Passthrough» там недоступні."
    }
  ],
  "tips": {
    "title": "Поради",
    "items": [
      "Із сервісами, які беруть плату за синтез мовлення, наприклад KizunaAI, кожен озвучуваний напрямок коштує дорожче. У KizunaAI «Переклад, який чую я» додає другий озвучуваний потік, і для запуску сеансу потрібен більший баланс.",
      "Якщо сервіс дає змогу вибрати голос, кожен озвучуваний рядок показує голос, яким він зачитується. Натисніть назву голосу, щоб змінити його.",
      "Коли «Переклад, який чує співрозмовник» вимкнено, співрозмовник нічого не чує від Sokuji. Щоб він міг читати, покажіть йому вікно субтитрів."
    ]
  },
  "faq": {
    "title": "Часті запитання",
    "items": [
      {
        "question": "Куди поділося «Лише текст»?",
        "answer": "Тепер це «Переклад, який чує співрозмовник», тільки навпаки: увімкнене «Лише текст» — це вимкнений «Переклад, який чує співрозмовник». Перемикач переїхав із розділу «Мови перекладу» в блок «Мовлення»."
      },
      {
        "question": "Чому я не можу змінити перемикач?",
        "answer": "Обидва зафіксовано, поки триває сеанс: спочатку зупиніть його. «Переклад, який чує співрозмовник» сірий у режимі «Співрозмовник», а «Переклад, який чую я» — у режимі «Я», бо цей напрямок не перекладається. У настільному застосунку «Переклад, який чую я» також сірий, поки в аудіо співрозмовника вибрано System Audio (All Applications): виберіть натомість застосунок."
      },
      {
        "question": "Чи припиняються всі витрати на мовлення, якщо вимкнути озвучування?",
        "answer": "Для більшості сервісів так: мовлення не синтезується. З Google Gemini аудіо все одно може створюватися й оплачуватися; Sokuji просто його не відтворює."
      }
    ]
  },
  "troubleshooting": {
    "title": "Усунення несправностей",
    "content": "Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Також можна запитати в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const it: TutorialData = {
  "pageTitle": "Voce o solo testo",
  "backLink": "Torna alla documentazione",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Panoramica",
    "content": "Ogni traduzione viene mostrata come testo. Due interruttori nel blocco <strong>Voce</strong> decidono quali vengono anche lette ad alta voce: <strong>Traduzione che sente l'altra parte</strong> legge la tua traduzione all’altra parte, e <strong>Traduzione che sento io</strong> legge a te la traduzione dell’altra parte. Disattivane uno quando in quella direzione bastano i sottotitoli, per esempio per seguire una riunione o sottotitolare il tuo intervento."
  },
  "steps": [
    {
      "title": "Trova gli interruttori",
      "content": "In Impostazioni, apri il blocco <strong>Voce</strong>: si trova in Rapido e, in Avanzato, nella scheda <strong>Audio</strong>. Entrambi gli interruttori sono fissi mentre una sessione è attiva e indicano «Fissato per questa sessione; fermala per cambiarlo.»",
      "screenshot": "01-text-only.png"
    },
    {
      "title": "Traduzione che sente l'altra parte",
      "content": "Attiva per impostazione predefinita, nelle modalità Io ed Entrambi: la tua traduzione viene letta ad alta voce nel microfono virtuale, così l’altra parte la sente nella riunione. Disattivala per avere solo i sottotitoli: al microfono virtuale non arriva nulla, e <strong>Lo sento anche io</strong> e <strong>Passthrough</strong> sono in grigio e muti. In modalità Altro la riga è in grigio."
    },
    {
      "title": "Traduzione che sento io",
      "content": "Usata nelle modalità Altro ed Entrambi, e disattivata per impostazione predefinita in una riunione. Attivala per farti leggere ad alta voce la traduzione dell’altra parte, sul dispositivo e sul canale che scegli in quella riga. Nell’app desktop richiede una sola applicazione come sorgente di <a href=\"/docs/features/participant-audio\">Audio dell'altro</a>, per esempio Microsoft Teams: con <strong>System Audio (All Applications)</strong> la riga è in grigio, perché la traduzione verrebbe catturata e tradotta di nuovo. Nell’estensione per browser è sempre disponibile. In modalità Io la riga è in grigio."
    },
    {
      "title": "Faccia a faccia",
      "content": "Con l’altra persona accanto a te (Entrambi, accanto a me), Traduzione che sento io è attiva per impostazione predefinita e ogni traduzione viene riprodotta in un auricolare: Traduzione che sente l'altra parte nell’orecchio dell’altra persona, non nel microfono virtuale, e Traduzione che sento io nel tuo. Lo sento anche io e Passthrough lì non sono disponibili."
    }
  ],
  "tips": {
    "title": "Consigli",
    "items": [
      "Con i servizi che fanno pagare il parlato generato, come KizunaAI, ogni direzione letta ad alta voce costa di più. Su KizunaAI, Traduzione che sento io aggiunge un secondo flusso vocale, e per avviare una sessione serve un saldo più alto.",
      "Se il servizio offre una scelta di voci, ogni riga letta ad alta voce mostra la voce che usa. Fai clic sul suo nome per cambiarla.",
      "Con Traduzione che sente l'altra parte disattivata, l’altra parte non sente nulla da Sokuji. Per farle leggere la traduzione, condividi la finestra dei sottotitoli."
    ]
  },
  "faq": {
    "title": "Domande frequenti",
    "items": [
      {
        "question": "Dov’è finito Solo testo?",
        "answer": "Ora è Traduzione che sente l'altra parte, al contrario: Solo testo attivo equivale a Traduzione che sente l'altra parte disattivata. Si è spostato da Lingue di Traduzione al blocco Voce."
      },
      {
        "question": "Perché non posso cambiare un interruttore?",
        "answer": "Entrambi sono fissi mentre una sessione è attiva: interrompila prima. Traduzione che sente l'altra parte è in grigio in modalità Altro e Traduzione che sento io in modalità Io, perché quella direzione non viene tradotta. Nell’app desktop, Traduzione che sento io è in grigio anche quando l’Audio dell'altro è System Audio (All Applications): scegli invece l’applicazione."
      },
      {
        "question": "Disattivare il parlato elimina tutti i costi del parlato?",
        "answer": "Per la maggior parte dei servizi sì: non viene generato alcun parlato. Con Google Gemini l’audio può comunque essere generato e addebitato; Sokuji semplicemente non lo riproduce."
      }
    ]
  },
  "troubleshooting": {
    "title": "Risoluzione dei problemi",
    "content": "Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Puoi anche chiedere su <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function FeatureTextOnly() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/features/text-only" />;
}
